import React, { useState } from 'react';

const BusinessWasteCalculator = () => {
  const [formData, setFormData] = useState({
    // Time Waste (Monthly Basis)
    monthlyRepetitiveTaskHours: 44, // 2 hours/day × 22 days
    averageHourlyRate: 50,
    numberOfEmployees: 3,
    
    // Energy Waste (Monthly Basis)
    monthlyElectricityBill: 800,
    monthlyWastedEnergyPercentage: 15, // Simplified to direct percentage
    
    // Material Waste (Monthly Basis)
    monthlyMaterialCosts: 2000,
    wastePercentage: 15,
    monthlyReorderCosts: 150, // Direct monthly cost instead of frequency
    
    // Digitization Savings Rate (Configurable)
    expectedSavingsPercentage: 70
  });

  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const calculateWasteReduction = () => {
    const { 
      monthlyRepetitiveTaskHours, averageHourlyRate, numberOfEmployees,
      monthlyElectricityBill, monthlyWastedEnergyPercentage,
      monthlyMaterialCosts, wastePercentage, monthlyReorderCosts,
      expectedSavingsPercentage
    } = formData;
    
    // Time Waste Calculations (Transparent Steps)
    const monthlyTimeWaste = monthlyRepetitiveTaskHours * numberOfEmployees * averageHourlyRate;
    const annualTimeWaste = monthlyTimeWaste * 12;
    
    // Energy Waste Calculations (Simplified and Clear)
    const monthlyEnergyWaste = monthlyElectricityBill * (monthlyWastedEnergyPercentage / 100);
    const annualEnergyWaste = monthlyEnergyWaste * 12;
    
    // Material Waste Calculations (Transparent)
    const monthlyMaterialWaste = monthlyMaterialCosts * (wastePercentage / 100);
    const annualMaterialWaste = monthlyMaterialWaste * 12;
    const annualReorderCosts = monthlyReorderCosts * 12;
    
    // Total Waste Calculations
    const totalMonthlyWaste = monthlyTimeWaste + monthlyEnergyWaste + monthlyMaterialWaste + monthlyReorderCosts;
    const totalAnnualWaste = annualTimeWaste + annualEnergyWaste + annualMaterialWaste + annualReorderCosts;
    
    // Digitization Savings (User-Configurable)
    const digitizationSavings = totalAnnualWaste * (expectedSavingsPercentage / 100);
    const monthlyDigitizationSavings = digitizationSavings / 12;

    setResults({
      // Detailed breakdown for transparency
      calculations: {
        timeWasteSteps: {
          monthlyHours: monthlyRepetitiveTaskHours,
          employees: numberOfEmployees,
          hourlyRate: averageHourlyRate,
          calculation: `${monthlyRepetitiveTaskHours} hours × ${numberOfEmployees} employees × $${averageHourlyRate}/hour`
        },
        energyWasteSteps: {
          monthlyBill: monthlyElectricityBill,
          wastePercentage: monthlyWastedEnergyPercentage,
          calculation: `$${monthlyElectricityBill} × ${monthlyWastedEnergyPercentage}%`
        },
        materialWasteSteps: {
          monthlyCosts: monthlyMaterialCosts,
          wastePercentage: wastePercentage,
          calculation: `$${monthlyMaterialCosts} × ${wastePercentage}%`
        }
      },
      timeWaste: {
        monthly: Math.round(monthlyTimeWaste),
        annual: Math.round(annualTimeWaste),
        totalHours: Math.round(monthlyRepetitiveTaskHours * numberOfEmployees * 12)
      },
      energyWaste: {
        monthly: Math.round(monthlyEnergyWaste),
        annual: Math.round(annualEnergyWaste)
      },
      materialWaste: {
        monthly: Math.round(monthlyMaterialWaste + monthlyReorderCosts),
        annual: Math.round(annualMaterialWaste + annualReorderCosts),
        wasteOnly: Math.round(monthlyMaterialWaste),
        reorderOnly: Math.round(monthlyReorderCosts)
      },
      totalWaste: {
        monthly: Math.round(totalMonthlyWaste),
        annual: Math.round(totalAnnualWaste)
      },
      potentialSavings: {
        monthly: Math.round(monthlyDigitizationSavings),
        annual: Math.round(digitizationSavings),
        percentage: expectedSavingsPercentage
      }
    });
    
    setShowResults(true);
  };

  return (
    <div id="roi-calculator" className="roi-calculator">
      <div className="container">
        <div className="row">
          <div className="col-md-12 text-center">
            <div className="section-title">
              <h2>Waste Estimator</h2>
              <p>Estimate how much money your business is losing to avoidable waste, and how much you could save.</p>
            </div>
          </div>
        </div>
        
        <div className="row">
          <div className="col-md-8 col-md-offset-2">
            <div className="calculator-form">
              <h3 style={{color: '#E0292A !important', marginBottom: '10px', marginTop: '10px'}}>Time Waste</h3>
              <p style={{color: '#666', fontSize: '14px', marginBottom: '25px'}}>
                Calculate monthly costs of repetitive manual tasks that could be automated
              </p>
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Monthly repetitive task hours (total across all involved employees)</label>
                    <input 
                      type="number" 
                      name="monthlyRepetitiveTaskHours"
                      className="form-control"
                      value={formData.monthlyRepetitiveTaskHours}
                      onChange={handleInputChange}
                      min="0"
                      step="0.5"
                    />
                    <small className="help-text">Example: 2 hrs/day × 22 days = 44 hrs/month</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Average hourly monetary rate (wages + overheads)</label>
                    <input 
                      type="number" 
                      name="averageHourlyRate"
                      className="form-control"
                      value={formData.averageHourlyRate}
                      onChange={handleInputChange}
                      min="1"
                    />
                    <small className="help-text">Including benefits, equipment costs, etc.</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Number of employees performing repetitive task</label>
                    <input 
                      type="number" 
                      name="numberOfEmployees"
                      className="form-control"
                      value={formData.numberOfEmployees}
                      onChange={handleInputChange}
                      min="1"
                    />
                    <small className="help-text">Employees doing repetitive tasks</small>
                  </div>
                </div>
              </div>
              
              <h3 style={{color: '#E0292A !important', marginBottom: '10px', marginTop: '10px'}}>Energy Waste</h3>
              <p style={{color: '#666', fontSize: '14px', marginBottom: '25px'}}>
                Estimate monthly costs from inefficient energy usage and equipment left running unnecessarily
              </p>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Monthly electricity bill</label>
                    <input 
                      type="number" 
                      name="monthlyElectricityBill"
                      className="form-control"
                      value={formData.monthlyElectricityBill}
                      onChange={handleInputChange}
                      min="1"
                    />
                    <small className="help-text">Your total monthly electricity costs (monetary amount)</small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Estimated energy waste percentage (%)</label>
                    <input 
                      type="number" 
                      name="monthlyWastedEnergyPercentage"
                      className="form-control"
                      value={formData.monthlyWastedEnergyPercentage}
                      onChange={handleInputChange}
                      min="0"
                      max="50"
                    />
                    <small className="help-text">% of bill from equipment left on, inefficient processes</small>
                  </div>
                </div>
              </div>
              
              <h3 style={{color: '#E0292A !important', marginBottom: '10px', marginTop: '10px'}}>Material Waste</h3>
              <p style={{color: '#666', fontSize: '14px', marginBottom: '25px'}}>
                Calculate costs from wasted materials, overordering, and emergency reorders due to poor planning
              </p>
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Monthly material costs</label>
                    <input 
                      type="number" 
                      name="monthlyMaterialCosts"
                      className="form-control"
                      value={formData.monthlyMaterialCosts}
                      onChange={handleInputChange}
                      min="0"
                    />
                    <small className="help-text">Raw materials, supplies, inventory (monetary amount)</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Estimated waste percentage (%)</label>
                    <input 
                      type="number" 
                      name="wastePercentage"
                      className="form-control"
                      value={formData.wastePercentage}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                    <small className="help-text">% lost to damage, expiry, overordering</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Monthly emergency reorder costs</label>
                    <input 
                      type="number" 
                      name="monthlyReorderCosts"
                      className="form-control"
                      value={formData.monthlyReorderCosts}
                      onChange={handleInputChange}
                      min="0"
                    />
                    <small className="help-text">Rush delivery fees, premium pricing (monetary amount)</small>
                  </div>
                </div>
              </div>
              
              <h3 style={{color: '#E0292A !important', marginBottom: '10px', marginTop: '10px'}}>Digital Transformation Implementation Level</h3>
              <p style={{color: '#666', fontSize: '14px', marginBottom: '25px'}}>
                Select your desired digital transformation implementation level
              </p>
              <div className="row">
                <div className="col-md-6 col-md-offset-3">
                  <div className="form-group text-center">
                    <label>DX level percentage (%)</label>
                    <input 
                      type="number" 
                      name="expectedSavingsPercentage"
                      className="form-control"
                      value={formData.expectedSavingsPercentage}
                      onChange={handleInputChange}
                      min="30"
                      max="90"
                    />
                    <small className="help-text">Conservative: 20-50%, Typical: 50-80%, Aggressive: 80-95%</small>
                  </div>
                </div>
              </div>
              
              <div className="text-center" style={{marginTop: '20px'}}>
                <button 
                  className="btn btn-custom btn-lg"
                  onClick={calculateWasteReduction}
                >
                  Show Estimates
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {showResults && results && (
          <div className="row roi-results" style={{marginTop: '40px'}}>
            <div className="col-md-12">
              <div className="results-container">
                <h3 className="text-center" style={{color: '#dc3545', marginBottom: '30px'}}>Your Estimated Business Monetary Waste:</h3>
                
                <div className="row" style={{marginBottom: '10px'}}>
                  <div className="col-md-12">
                    <div className="total-waste-banner">
                      <div className="waste-single-line">
                        ${results.totalWaste.annual.toLocaleString()} Annually (${results.totalWaste.monthly.toLocaleString()} Monthly)
                      </div>
                    </div>
                  </div>
                </div>
                
                <h3 className="text-center" style={{marginBottom: '10px'}}>Detailed Breakdown & Calculations:</h3>
                
                <div className="calculation-breakdown" style={{backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '10px', marginBottom: '10px'}}>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="calc-step">
                        <h6 style={{color: '#E0292A', marginBottom: '10px', fontSize: '15px'}}>Time Waste:</h6>
                        <p style={{fontSize: '15px', color: '#555', marginBottom: '5px'}}>
                          {results.calculations.timeWasteSteps.calculation}
                        </p>
                        <p style={{fontSize: '15px', color: '#555', marginBottom: '10px'}}>
                          = <strong>${results.timeWaste.monthly.toLocaleString()}/month</strong>
                        </p>
                        <p style={{fontSize: '14px', color: '#666'}}>
                          Annual: ${results.timeWaste.annual.toLocaleString()} ({results.timeWaste.totalHours.toLocaleString()} hours)
                        </p>
                      </div>
                    </div>
                    
                    <div className="col-md-4">
                      <div className="calc-step">
                        <h6 style={{color: '#E0292A', marginBottom: '10px', fontSize: '15px'}}>Energy Waste:</h6>
                        <p style={{fontSize: '15px', color: '#555', marginBottom: '5px'}}>
                          {results.calculations.energyWasteSteps.calculation}
                        </p>
                        <p style={{fontSize: '15px', color: '#555', marginBottom: '10px'}}>
                          = <strong>${results.energyWaste.monthly.toLocaleString()}/month</strong>
                        </p>
                        <p style={{fontSize: '14px', color: '#666'}}>
                          Annual: ${results.energyWaste.annual.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="col-md-4">
                      <div className="calc-step">
                        <h6 style={{color: '#E0292A', marginBottom: '10px', fontSize: '15px'}}>Material Waste:</h6>
                        <p style={{fontSize: '15px', color: '#555', marginBottom: '5px'}}>
                          {results.calculations.materialWasteSteps.calculation}
                        </p>
                        <p style={{fontSize: '15px', color: '#555', marginBottom: '5px'}}>
                          + ${results.materialWaste.reorderOnly}/month (reorders)
                        </p>
                        <p style={{fontSize: '15px', color: '#555', marginBottom: '10px'}}>
                          = <strong>${results.materialWaste.monthly.toLocaleString()}/month</strong>
                        </p>
                        <p style={{fontSize: '14px', color: '#666'}}>
                          Annual: ${results.materialWaste.annual.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-4">
                    <div className="waste-metric time-waste">
                      <div className="metric-value">
                        ${results.timeWaste.annual.toLocaleString()}
                      </div>
                      <div className="metric-label" style={{fontSize: '1.2rem'}}>Time Waste (Annual)</div>
                      <div className="metric-detail" style={{fontSize: '1.4rem'}}>{results.timeWaste.totalHours.toLocaleString()} hours lost</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="waste-metric energy-waste">
                      <div className="metric-value">
                        ${results.energyWaste.annual.toLocaleString()}
                      </div>
                      <div className="metric-label" style={{fontSize: '1.2rem'}}>Energy Waste (Annual)</div>
                      <div className="metric-detail" style={{fontSize: '1.4rem'}}>${results.energyWaste.monthly.toLocaleString()} per month</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="waste-metric material-waste">
                      <div className="metric-value">
                        ${results.materialWaste.annual.toLocaleString()}
                      </div>
                      <div className="metric-label" style={{fontSize: '1.2rem'}}>Material Waste (Annual)</div>
                      <div className="metric-detail" style={{fontSize: '1.4rem'}}>${results.materialWaste.monthly.toLocaleString()} per month</div>
                    </div>
                  </div>
                </div>
                
                <div className="row" style={{marginTop: '10px'}}>
                  <div className="col-md-12 text-center">
                    <div className="savings-potential">
                      <h3 style={{color: '#28a745', marginBottom: '20px'}}>Good News! Here's What You Could Save:</h3>
                      
                      <div style={{backgroundColor: 'rgba(255,255,255,0.2)', padding: '10px', borderRadius: '8px', marginBottom: '10px'}}>
                        <p style={{fontSize: '15px', color: 'white', marginBottom: '10px', fontStyle: 'italic'}}>
                          <strong>Our Calculation:</strong> ${results.totalWaste.annual.toLocaleString()} annual waste × {results.potentialSavings.percentage}% selected efficiency improvement through digital transformation = savings below
                        </p>
                      </div>
                      
                      <div className="potential-savings">
                        <div className="savings-amount">
                          ${results.potentialSavings.annual.toLocaleString()} Annually (${results.potentialSavings.monthly.toLocaleString()} Monthly)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row" style={{marginTop: '30px'}}>
                  <div className="col-md-12 text-center">
                    <div className="cta-section">
                      <p style={{color: '#495057', marginBottom: '5px'}}>
                        Stop throwing your hard-earned money away every day!
                      </p>
                      <p>Let's discuss how digital transformation can plug these money leaks in your business and keep it viable well into the future!</p>
                      <a href="#contact" className="btn btn-success btn-lg">
                        Get Started Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <style jsx>{`
        .roi-calculator {
          padding: 60px 0 20px 0;
          background: #E2E8F0;
        }
        
        .calculator-form h3 {
          color: #E0292A !important;
        }
        
        .total-waste-banner {
          background: #D42C2C;
          color: white;
          padding: 15px;
          border-radius: 8px;
          text-align: center;
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        
        .waste-single-line {
          font-size: 2.2rem;
          font-weight: 700;
          letter-spacing: 1px;
        }
        
        .waste-metric {
          background: white;
          padding: 10px;
          border-radius: 8px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          text-align: center;
          margin-bottom: 10px;
          border-left: 3px solid #ddd;
        }
        
        .waste-metric.time-waste {
          border-left-color: var(--yuccan-red, #D42C2C);
        }
        
        .waste-metric.energy-waste {
          border-left-color: var(--warning-orange, #E67E22);
        }
        
        .waste-metric.material-waste {
          border-left-color: var(--success-green, #2D7A3E);
        }
        
        .metric-detail {
          font-size: 1.1rem;
          color: #6c757d;
          margin-top: 5px;
        }
        
        .savings-potential {
          background: #40c057;
          padding: 15px;
          border-radius: 8px;
          color: white;
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        
        .savings-amount {
          font-size: 2.2rem;
          font-weight: 700;
          margin-bottom: 5px;
          letter-spacing: 1px;
        }
        
        .savings-label {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 3px;
          letter-spacing: 1px;
        }
        
        .savings-monthly {
          font-size: 1.2rem;
          opacity: 0.9;
          margin-bottom: 5px;
        }
        
        .savings-percentage {
          font-size: 1.3rem;
          font-weight: 500;
          background: rgba(255,255,255,0.2);
          padding: 4px 10px;
          border-radius: 15px;
          display: inline-block;
        }
        
        .cta-section {
          background: #F7FAFC;
          padding: 15px;
          border-radius: 8px;
          border: 2px dashed #2B4B8C;
        }
        
        .calculator-form {
          background: white;
          padding: 40px;
          border-radius: 10px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          margin-bottom: 30px;
        }
        
        .form-group {
          margin-bottom: 25px;
        }
        
        .form-group label {
          font-weight: 600;
          color: #495057;
          margin-bottom: 8px;
          display: block;
          font-size: 12px !important;
        }
        
        .form-control {
          border: 2px solid #e9ecef;
          border-radius: 5px;
          padding: 12px 15px;
          font-size: 13px !important;
          transition: border-color 0.3s ease;
        }
        
        input[type="number"] {
          font-size: 13px !important;
        }
        
        .form-control:focus {
          border-color: #007bff;
          box-shadow: none;
        }
        
        .help-text {
          color: #6c757d !important;
          font-size: 12px !important;
          font-style: italic;
          margin-top: 5px;
          display: block;
          white-space: nowrap;
        }
        
        .form-group label {
          font-weight: 600;
          color: #495057;
          margin-bottom: 8px;
          display: block;
          font-size: 12px !important;
        }
        
        @media (min-width: 769px) {
          .form-group .help-text {
            white-space: normal;
            word-wrap: break-word;
            text-align: left;
            line-height: 1.4;
          }
        }
        
        @media (max-width: 768px) {
          .help-text {
            white-space: normal;
            word-wrap: break-word;
            text-align: center;
          }
        }
        
                
        .results-container {
          background: white;
          padding: 15px;
          border-radius: 8px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.1);
        }
        
        .metric-value {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 3px;
        }
        
        .metric-label {
          font-size: 0.9rem;
          opacity: 0.9;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .btn-primary {
          background: #2B4B8C;
          border: none;
          padding: 15px 40px;
          font-size: 18px;
          font-weight: 600;
          font-family: var(--font-headers, 'Poppins', sans-serif);
          border-radius: 50px;
          transition: transform 0.3s ease;
        }
        
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        }
        
        .btn-success {
          background: #40c057;
          border: none;
          padding: 15px 40px;
          font-size: 18px;
          font-weight: 600;
          font-family: var(--font-headers, 'Poppins', sans-serif);
          border-radius: 50px;
          color: #000 !important;
          text-transform: uppercase;
          transition: all 0.3s ease;
        }
        
        .btn-success:hover {
          color: #fff !important;
        }
        
        @media (max-width: 768px) {
          .roi-calculator {
            padding: 40px 0;
          }
          
          .calculator-form, .results-container {
            padding: 25px;
          }
          
          .metric-value {
            font-size: 2rem;
          }
          
          .form-control {
            font-size: 13px !important;
          }
          
          input[type="number"] {
            font-size: 13px !important;
          }
          
                    
          .form-group label {
            font-size: 12px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BusinessWasteCalculator;